import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone as PhoneIcon } from "lucide-react";

export const Layout = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Hide the mobile sticky CTA on /contact (already there) and /admin routes.
  const hideStickyCTA =
    location.pathname.startsWith("/contact") ||
    location.pathname.startsWith("/admin");

  const navLinks = [
    { path: "/services", label: "Planning Services" },
    { path: "/blog", label: "Blog" },
    { path: "/faqs", label: "FAQ & Resources" },
    { path: "/testimonials", label: "Testimonials" },
    { path: "/team", label: "Team" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-ink text-white font-body">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Header — non-sticky, scrolls away with the page */}
      <header className="bg-ink relative z-40" data-testid="header">
        {/* Top Nav Bar — links spread left-to-right across the very top */}
        <nav
          className="hidden md:block border-b border-neutral-800 bg-ink"
          data-testid="desktop-nav"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-xs lg:text-sm uppercase tracking-widest font-medium ${
                    isActive(link.path)
                      ? "text-white"
                      : "text-gray-400 hover:text-white"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2 absolute right-4 top-4 z-10"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Menu className="w-6 h-6 text-white" />
          )}
        </button>

        {/* Logo — centred, prominent, below the nav */}
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center pt-4 pb-6 md:pt-6 md:pb-8">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_nz-dev-planning/artifacts/5itrgm9h_code_logo_1tier.png"
                alt="Code Planning Limited"
                className="h-32 md:h-56 lg:h-72 w-auto invert max-w-full"
              />
            </Link>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            className="md:hidden bg-ink border-t border-neutral-800"
            data-testid="mobile-nav"
          >
            <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm uppercase tracking-widest font-medium py-2 ${
                    isActive(link.path)
                      ? "text-white"
                      : "text-gray-400 hover:text-white"
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content — no top padding because header is not fixed.
          Extra bottom padding on mobile only, to sit above the sticky CTA bar. */}
      <main className={hideStickyCTA ? "" : "pb-16 md:pb-0"}>
        <Outlet />
      </main>

      {/* Mobile-only sticky bottom CTA bar */}
      {!hideStickyCTA && (
        <div
          className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-ink/95 backdrop-blur border-t border-neutral-800 flex items-stretch"
          data-testid="mobile-sticky-cta"
        >
          <Link
            to="/contact#property-form"
            className="flex-1 bg-white text-ink px-4 py-3 uppercase tracking-widest text-xs font-bold text-center flex items-center justify-center"
            data-testid="mobile-sticky-review"
          >
            Check My Property
          </Link>
          <a
            href="tel:+64211106525"
            className="w-14 flex items-center justify-center border-l border-neutral-800 text-white active:bg-neutral-900"
            aria-label="Call Code Planning"
            data-testid="mobile-sticky-call"
          >
            <PhoneIcon className="w-5 h-5" />
          </a>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-ink text-white py-10 md:py-14" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <h3 className="font-heading text-2xl font-bold mb-4">
                Code Planning
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                Resource Consents. Simplified.
                <br />
                A small, senior planning team based on Auckland's North Shore — treating every project like our own.
                <br />
                <br />
                <span className="text-gray-300">Most of our work is in Auckland, Kaipara and Whangārei — and we prepare consents all over Aotearoa.</span>
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm uppercase tracking-widest mb-4 font-bold">
                Quick Links
              </h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm uppercase tracking-widest mb-4 font-bold">
                Contact
              </h4>
              <ul className="space-y-2 text-gray-400">
                <li>39 Anzac Road, Browns Bay</li>
                <li>Auckland, New Zealand</li>
                <li>
                  <a href="mailto:mills@codeplanning.co.nz" className="hover:text-white transition-colors">
                    mills@codeplanning.co.nz
                  </a>
                </li>
                <li>
                  <a href="tel:+64211106525" className="hover:text-white transition-colors">
                    021 110 6525
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} Code Planning Limited. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
